import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DragDropContext, Droppable } from '@hello-pangea/dnd';
import {
    useMoveProjectTagMutation,
    useMoveProductRowMutation,
} from '../../store';
import { TagRow } from './TagRow';
import { TagQtyChangeWarningModal } from '../modals/TagQtyChangeWarningModal';
import { TagNameChangeWarningModal } from '../modals/TagNameChangeWarningModal';
import { ChevronButton } from '../../components';

const getComparableValue = (tag, key) => {
    if (key === 'quantity') {
        return (tag?.quantity ?? '').toString();
    }

    return (tag?.[key] ?? '').toString();
};

const compareAlphanumeric = (a, b) =>
    a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: 'base',
    });

const SORTABLE_COLUMNS = [
    { key: 'value', label: 'Tag', className: 'pl-1 pr-4' },
    { key: 'equipmentType', label: 'Equipment Type', className: 'px-4' },
    { key: 'room', label: 'Room', className: 'px-4' },
    { key: 'trade', label: 'Trade', className: 'px-4' },
    { key: 'quantity', label: 'Quantity', className: 'w-20' },
];

const getSortIcon = (direction) => {
    if (direction === 'asc') {
        return '↓';
    }

    if (direction === 'desc') {
        return '↑';
    }

    return '↕';
};

export const TagsTable = (props) => {
    const {
        tags,
        projectId,
        isDraggable = false,
        checkedItemIds = new Set(),
        checkedTagIds = new Set(),
        onItemCheckboxToggle,
        onTagCheckboxToggle,
    } = props;
    const [localTags, setLocalTags] = useState(tags);
    const [changeTagQtyFail, setChangeTagQtyFail] = useState(false);
    const [changeTagNameFail, setChangeTagNameFail] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [allOpen, setAllOpen] = useState(false);
    const [sortPriorities, setSortPriorities] = useState([]);
    const [moveProjectTag] = useMoveProjectTagMutation();
    const [moveProductRow] = useMoveProductRowMutation();

    useEffect(() => {
        if (!isDragging) {
            setLocalTags(tags);
        }
    }, [tags, isDragging]);

    const handleDragEnd = async (result) => {
        const { source, destination, type, draggableId } = result;

        if (!destination) {
            return;
        }

        const sourceIndex = source.index;
        const destinationIndex = destination.index;
        const sourceDroppableId = source.droppableId;
        const destinationDroppableId = destination.droppableId;

        if (
            sourceIndex === destinationIndex &&
            sourceDroppableId === destinationDroppableId
        ) {
            return;
        }

        if (type === 'TAG') {
            try {
                const reorderedTags = Array.from(localTags);
                const [movedTag] = reorderedTags.splice(sourceIndex, 1);
                reorderedTags.splice(destinationIndex, 0, movedTag);
                setLocalTags(reorderedTags);
                await moveProjectTag({
                    id: projectId,
                    tag: {
                        TagId: draggableId,
                        NewIndex: destinationIndex,
                    },
                }).unwrap();
            } catch (err) {
                console.error('Failed to move tag:', err);
            }
        } else if (type === 'PRODUCT') {
            const sourceTag = localTags.find((t) => t.id === sourceDroppableId);
            const destinationTag = localTags.find(
                (t) => t.id === destinationDroppableId,
            );

            if (!sourceTag || !destinationTag) {
                setIsDragging(false);
                return;
            }

            const sourceItems = Array.from(sourceTag.items);
            const [itemToMove] = sourceItems.splice(sourceIndex, 1);
            const destinationItems =
                sourceDroppableId === destinationDroppableId
                    ? sourceItems
                    : Array.from(destinationTag.items);

            destinationItems.splice(destinationIndex, 0, itemToMove);
            const newLocalTags = localTags.map((tag) => {
                let newTag = tag;
                if (tag.id === sourceDroppableId) {
                    newTag = { ...tag, items: sourceItems };
                }
                if (tag.id === destinationDroppableId) {
                    newTag = { ...tag, items: destinationItems };
                }
                return newTag;
            });
            setLocalTags(newLocalTags);
            try {
                await moveProductRow({
                    id: projectId,
                    product: {
                        SourceTagId: sourceDroppableId,
                        DestinationTagId: destinationDroppableId,
                        ItemId: draggableId,
                        NewIndex: destinationIndex,
                    },
                }).unwrap();
            } catch (err) {
                console.error('Failed to move product:', err);
            }
        }
        setIsDragging(false);
    };

    const handleTagCheckboxToggle = useCallback(
        (tagId, shouldCheckAll) => {
            const tag = localTags.find((t) => t.id === tagId);
            onTagCheckboxToggle?.(tagId, shouldCheckAll, tag?.items ?? []);
        },
        [localTags, onTagCheckboxToggle],
    );

    const toggleSort = useCallback((key) => {
        setSortPriorities((prev) => {
            const current = prev.find((x) => x.key === key);

            if (!current) {
                return [{ key, direction: 'asc' }, ...prev];
            }

            if (current.direction === 'asc') {
                return [
                    { key, direction: 'desc' },
                    ...prev.filter((x) => x.key !== key),
                ];
            }

            return prev.filter((x) => x.key !== key);
        });
    }, []);

    const getSortDirection = useCallback(
        (key) => sortPriorities.find((x) => x.key === key)?.direction ?? null,
        [sortPriorities],
    );

    const sortedTags = useMemo(() => {
        if (!localTags?.length || sortPriorities.length === 0) {
            return localTags ?? [];
        }

        const fallbackOrder = new Map(
            localTags.map((tag, idx) => [tag.id, idx]),
        );

        return [...localTags].sort((a, b) => {
            for (const criterion of sortPriorities) {
                const aVal = getComparableValue(a, criterion.key);
                const bVal = getComparableValue(b, criterion.key);
                const cmp = compareAlphanumeric(aVal, bVal);

                if (cmp !== 0) {
                    return criterion.direction === 'asc' ? cmp : -cmp;
                }
            }

            return (
                (fallbackOrder.get(a.id) ?? 0) - (fallbackOrder.get(b.id) ?? 0)
            );
        });
    }, [localTags, sortPriorities]);

    return (
        <>
            <DragDropContext
                onBeforeCapture={() => setIsDragging(true)}
                onDragEnd={handleDragEnd}
            >
                <Droppable
                    droppableId="tags-table"
                    type="TAG"
                >
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                        >
                            <table className="tags-table">
                                <thead className="text-black-30">
                                    <tr>
                                        <th className="">
                                            <ChevronButton
                                                open={allOpen}
                                                onClick={() =>
                                                    setAllOpen(!allOpen)
                                                }
                                            />
                                        </th>
                                        <th className="px-2 w-1">{'\xa0'}</th>
                                        {SORTABLE_COLUMNS.slice(0, 4).map(
                                            ({ key, label, className }) => (
                                                <th
                                                    key={key}
                                                    className={className}
                                                >
                                                    <button
                                                        type="button"
                                                        className="swegon-like-button white inline-flex items-center gap-1 font-bold"
                                                        style={{
                                                            color: getSortDirection(
                                                                key,
                                                            )
                                                                ? 'black'
                                                                : undefined,
                                                        }}
                                                        onClick={() =>
                                                            toggleSort(key)
                                                        }
                                                    >
                                                        <span>{label}</span>
                                                        <span>
                                                            {getSortIcon(
                                                                getSortDirection(
                                                                    key,
                                                                ),
                                                            )}
                                                        </span>
                                                    </button>
                                                </th>
                                            ),
                                        )}
                                        <th className="">{'\xa0'}</th>
                                        <th className="w-20">
                                            <button
                                                type="button"
                                                className="swegon-like-button white inline-flex items-center gap-1 font-bold"
                                                style={{
                                                    color: getSortDirection(
                                                        'quantity',
                                                    )
                                                        ? 'black'
                                                        : undefined,
                                                }}
                                                onClick={() =>
                                                    toggleSort('quantity')
                                                }
                                            >
                                                <span>Quantity</span>
                                                <span>
                                                    {getSortIcon(
                                                        getSortDirection(
                                                            'quantity',
                                                        ),
                                                    )}
                                                </span>
                                            </button>
                                        </th>
                                        <th className="">{'\xa0'}</th>
                                    </tr>
                                </thead>
                                {sortedTags.map((tag, index) => (
                                    <TagRow
                                        allOpen={allOpen}
                                        key={tag.id}
                                        isDraggable={
                                            isDraggable &&
                                            sortPriorities.length === 0
                                        }
                                        tag={tag}
                                        projectId={projectId}
                                        index={index}
                                        OnTagQtyChangeFail={setChangeTagQtyFail}
                                        OnTagNameChangeFail={
                                            setChangeTagNameFail
                                        }
                                        checkedItemIds={checkedItemIds}
                                        checkedTagIds={checkedTagIds}
                                        onItemCheckboxToggle={
                                            onItemCheckboxToggle
                                        }
                                        onTagCheckboxToggle={
                                            handleTagCheckboxToggle
                                        }
                                    />
                                ))}
                                {provided.placeholder}
                            </table>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <TagQtyChangeWarningModal
                show={changeTagQtyFail}
                onClose={() => setChangeTagQtyFail(false)}
            />
            <TagNameChangeWarningModal
                show={changeTagNameFail}
                onClose={() => setChangeTagNameFail(false)}
            />
        </>
    );
};
